import { useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import { albumExtensions } from "Helpers/File";

import UseFile from "Hooks/UseFile";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import FileUploader from "Components/Admin/FileUploader";

import * as S from "./styles";

/*
 * Envio para o ÁLBUM pela tela do ADMIN (05/09/2026).
 *
 * A sessão do admin só moderava: listava e aprovava/reprovava o que o
 * proponente mandava. Faltava a própria equipe conseguir subir material — e o
 * backend já aceitava (`uploadGalleryImage` e `addAlbumLink` não barram admin),
 * só não havia por onde.
 *
 * ⚠️ Item enviado pelo ADMIN nasce **Aprovada**, não Pendente: não há o que
 * moderar no próprio material da equipe. Por isso ele aparece na lista já
 * publicado, sem os botões de avaliação — e não gera e-mail de aviso (o admin é
 * o destinatário deles).
 *
 * Componente próprio, e não o `GalleryUpload` da área comum: aquele é um modal
 * de fundo escuro (`neutral.black` com texto branco), feito para a área do
 * usuário. Aqui a superfície é clara — é o gotcha de tema do CLAUDE.md §9.
 */
const AdminGalleryUpload = ({ projectId, reload }) => {
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();
  const { uploadFile } = UseFile();

  const [files, setFiles] = useState([]);
  const [linkName, setLinkName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [abortController, setAbortController] = useState(null);

  const submitLink = async () => {
    setLinkError(null);

    // Eco da validação do backend: protocolo em lista fechada, senão o `href`
    // da tela aceitaria `javascript:`.
    if (!/^https?:\/\//i.test(linkUrl.trim()))
      return setLinkError("Informe um link começando com http:// ou https://");

    if (linkName.trim().length < 2)
      return setLinkError("Informe um nome para o link");

    setLoading(true);

    try {
      await api.post("/projects/album/link", {
        project_id: projectId,
        name: linkName.trim(),
        url: linkUrl.trim(),
      });

      setLinkName("");
      setLinkUrl("");
      showNotification("success", "Sucesso!", "Link adicionado ao Álbum!");

      if (reload) reload();
    } catch (error) {
      return HTTPErrorCallback(error);
    } finally {
      setLoading(false);
    }
  };

  const submitFiles = async () => {
    if (!files.length) return;

    setLoading(true);

    const controller = new AbortController();
    setAbortController(controller);
    setProgress(0);

    try {
      const files_to_send = [];

      for (const file of files) {
        const location = file.file_src
          ? file.file_src
          : await uploadFile(file?.file, undefined, {
              onProgress: setProgress,
              signal: controller.signal,
            });

        files_to_send.push({ name: file.name, file_src: location });
      }

      await api.post("/projects/gallery/upload", {
        project_id: projectId,
        files: files_to_send,
      });

      setFiles([]);
      showNotification("success", "Sucesso!", "Arquivos enviados ao Álbum!");

      if (reload) reload();
    } catch (error) {
      // Cancelar é decisão do usuário: não é erro para notificar.
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError")
        return;

      return HTTPErrorCallback(error);
    } finally {
      setLoading(false);
      setProgress(null);
      setAbortController(null);
    }
  };

  return (
    <S.Wrapper>
      <S.Section>
        <span>Adicionar ao Álbum</span>
        <hr />
      </S.Section>

      <FileUploader
        allowMultiple={false}
        label="Arquivo"
        loading={loading}
        allowedExtensions={albumExtensions}
        progress={progress}
        setFiles={(file) =>
          setFiles((prev) => [...prev, { file: file[0], name: file[0].name }])
        }
      />

      {files.map((file) => (
        <S.File key={file.name}>
          <span>{file.name}</span>
          <Button
            type="button"
            outline
            text="Remover"
            disabled={loading}
            onClick={() =>
              setFiles((prev) => prev.filter((f) => f.name !== file.name))
            }
          />
        </S.File>
      ))}

      {files.length ? (
        <S.Buttons>
          <Button
            type="button"
            text="Enviar arquivos"
            disabled={loading}
            onClick={submitFiles}
          />

          {abortController ? (
            <Button
              type="button"
              outline
              variant="critical"
              text="Cancelar envio"
              onClick={() => abortController.abort()}
            />
          ) : null}
        </S.Buttons>
      ) : null}

      <S.Section>
        <span>Ou adicionar link</span>
        <hr />
      </S.Section>

      <S.LinkHint>
        Para vídeos grandes, prefira publicar no YouTube ou Vimeo e colar o link
        aqui — o envio é imediato e não consome o limite de arquivo.
      </S.LinkHint>

      <S.LinkFields>
        <input
          type="text"
          placeholder="Nome do link (ex.: Making of do espetáculo)"
          value={linkName}
          onChange={(event) => setLinkName(event.target.value)}
        />
        <input
          type="url"
          placeholder="https://www.youtube.com/watch?v=..."
          value={linkUrl}
          onChange={(event) => setLinkUrl(event.target.value)}
        />

        {linkError ? <S.LinkError>{linkError}</S.LinkError> : null}

        <S.Buttons>
          <Button
            type="button"
            outline
            text="Adicionar link"
            disabled={loading || !linkName || !linkUrl}
            onClick={submitLink}
          />
        </S.Buttons>
      </S.LinkFields>
    </S.Wrapper>
  );
};

export default AdminGalleryUpload;
